import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Tooltip, Typography, Chip, Alert, Link, Checkbox, FormControlLabel,
} from '@mui/material';
import { selectUser } from '../../store/slices/authSlice';
import { formatMoney } from '../../utils/currency';
import {
  confirmGroceryOrder, markGroceryOrderReady, cancelGroceryOrder,
  refundGroceryOrder, assignGroceryDeliveryPartner,
} from '../../services/businessService';

interface OrderRow {
  _id: string;
  status: string;
  paymentStatus?: string;
  paymentMethod?: string;
  total?: number;
  deliveryPartnerId?: string | { _id?: string };
  customerId?: string | { _id?: string };
  metadata?: { partialRefund?: number; refundDestination?: string; refundAmount?: number };
}

interface Props {
  order: OrderRow;
  onUpdated: () => void;
  showAdminExtras?: boolean;
}

const REFUND_ELIGIBILITY =
  'Refund is available only for Delivered or Cancelled orders when payment status is Completed and the order is not already refunded. '
  + 'Cash/COD and wallet refunds credit the customer wallet. Online payments refund to the original method. '
  + 'Refunds are allowed within the Refund Window (Site Settings → Payments) after the order closed; later refunds need a Super Admin override with a reason.';

function customerIdOf(order: OrderRow): string | undefined {
  if (!order.customerId) return undefined;
  if (typeof order.customerId === 'string') return order.customerId;
  return order.customerId._id;
}

function isAlreadyRefunded(order: OrderRow) {
  return order.status === 'refunded' || order.paymentStatus === 'refunded';
}

function canRefundOrder(order: OrderRow) {
  if (isAlreadyRefunded(order)) return false;
  if (!['delivered', 'cancelled'].includes(order.status)) return false;
  return order.paymentStatus === 'completed';
}

function refundDestinationHint(order: OrderRow): string {
  const method = String(order.paymentMethod || '').toLowerCase();
  if (['card', 'upi', 'razorpay', 'stripe', 'online'].includes(method)) {
    return `Refund returns to the customer’s original ${method.toUpperCase()} payment method.`;
  }
  if (method === 'wallet') {
    return 'Refund credits the customer wallet — open their profile to confirm the balance.';
  }
  return 'Refund credits the customer wallet (cash/COD) — open their profile to confirm the balance.';
}

function refundUnavailableReason(order: OrderRow): string {
  if (isAlreadyRefunded(order)) {
    const amt = order.metadata?.refundAmount ?? order.metadata?.partialRefund ?? order.total;
    const dest = order.metadata?.refundDestination === 'original_payment'
      ? 'original payment method'
      : 'customer wallet';
    return `Already Refunded${amt != null ? ` (${formatMoney(amt)})` : ''} via ${dest}. Another refund is not allowed.`;
  }
  if (!['delivered', 'cancelled'].includes(order.status)) {
    return `Refund unavailable while status is "${order.status}". Only Delivered or Cancelled orders with Completed payment can be refunded.`;
  }
  if (order.paymentStatus !== 'completed') {
    return `Refund unavailable — payment status is "${order.paymentStatus || 'pending'}" (needs Completed).`;
  }
  return REFUND_ELIGIBILITY;
}

export default function GroceryOrderLifecycleActions({ order, onUpdated, showAdminExtras }: Props) {
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  const userType = user?.userType;
  const [loading, setLoading] = useState<string | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [overrideWindow, setOverrideWindow] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [partnerId, setPartnerId] = useState('');
  const [refundNotice, setRefundNotice] = useState<string | null>(null);
  const [lastRefundCustomerId, setLastRefundCustomerId] = useState<string | undefined>();

  const run = async (key: string, fn: () => Promise<unknown>) => {
    setLoading(key);
    try {
      await fn();
      onUpdated();
    } finally {
      setLoading(null);
    }
  };

  const isAdmin = userType === 'client_admin' || userType === 'super_admin';
  const customerId = customerIdOf(order);
  const refundEligible = canRefundOrder(order);
  const alreadyRefunded = isAlreadyRefunded(order);

  const openCustomerProfile = (id?: string) => {
    const cid = id || customerId;
    if (!cid) return;
    navigate(`/customers/${cid}`, { state: { from: window.location.pathname } });
  };

  let roleActions: ReactNode = null;

  if (userType === 'merchant' && ['pending', 'placed'].includes(order.status)) {
    roleActions = (
      <Box display="flex" gap={0.5}>
        <Button
          size="small"
          variant="contained"
          color="success"
          disabled={!!loading}
          onClick={() => run('confirm', () => confirmGroceryOrder(order._id, true))}
        >
          Accept
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="error"
          disabled={!!loading}
          onClick={() => run('reject', () => confirmGroceryOrder(order._id, false))}
        >
          Reject
        </Button>
      </Box>
    );
  } else if (userType === 'merchant' && ['confirmed', 'preparing'].includes(order.status)) {
    roleActions = (
      <Button
        size="small"
        variant="contained"
        disabled={!!loading}
        onClick={() => run('ready', () => markGroceryOrderReady(order._id))}
      >
        Mark Ready
      </Button>
    );
  } else if (
    (userType === 'merchant' || userType === 'client_admin' || userType === 'super_admin')
    && !['delivered', 'cancelled', 'refunded'].includes(order.status)
  ) {
    roleActions = (
      <Button size="small" variant="outlined" color="error" onClick={() => setCancelOpen(true)}>
        Cancel
      </Button>
    );
  }

  let adminExtras: ReactNode = null;
  if (showAdminExtras && isAdmin) {
    const bits: ReactNode[] = [];
    if (!order.deliveryPartnerId && !['delivered', 'cancelled', 'refunded'].includes(order.status)) {
      bits.push(
        <Button key="assign" size="small" variant="outlined" onClick={() => setAssignOpen(true)}>Assign</Button>,
      );
    }
    if (refundEligible) {
      bits.push(
        <Tooltip key="refund" title={`${REFUND_ELIGIBILITY} ${refundDestinationHint(order)}`} arrow>
          <span>
            <Button size="small" color="warning" variant="outlined" onClick={() => setRefundOpen(true)}>
              Refund
            </Button>
          </span>
        </Tooltip>,
      );
    } else if (['delivered', 'cancelled', 'refunded'].includes(order.status)) {
      bits.push(
        <Tooltip key="refund-status" title={refundUnavailableReason(order)} arrow>
          <Chip
            size="small"
            variant={alreadyRefunded ? 'filled' : 'outlined'}
            color={alreadyRefunded ? 'success' : 'default'}
            label={alreadyRefunded ? 'Already Refunded' : 'Refund not eligible'}
            sx={{ fontWeight: 700 }}
            onClick={alreadyRefunded && customerId ? () => openCustomerProfile() : undefined}
          />
        </Tooltip>,
      );
    }
    if (bits.length) {
      adminExtras = <>{bits}</>;
    }
  }

  if (!roleActions && !adminExtras) {
    if (['cancelled', 'delivered', 'refunded'].includes(order.status)) {
      return (
        <Tooltip title={refundUnavailableReason(order)} arrow>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={600}
            sx={alreadyRefunded && customerId ? { cursor: 'pointer', textDecoration: 'underline' } : undefined}
            onClick={alreadyRefunded && customerId ? () => openCustomerProfile() : undefined}
          >
            {alreadyRefunded ? 'Already Refunded' : 'No actions'}
          </Typography>
        </Tooltip>
      );
    }
    return null;
  }

  return (
    <>
      <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
        {roleActions}
        {adminExtras}
      </Box>
      {refundNotice && (
        <Alert severity="success" sx={{ mt: 0.5, py: 0, '& .MuiAlert-message': { py: 0.5 } }}>
          <Typography variant="caption" fontWeight={700} display="block">
            {refundNotice}
          </Typography>
          {(lastRefundCustomerId || customerId) && (
            <Link
              component="button"
              type="button"
              variant="caption"
              fontWeight={800}
              onClick={() => openCustomerProfile(lastRefundCustomerId)}
            >
              Open customer profile / wallet
            </Link>
          )}
        </Alert>
      )}

      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Cancel grocery order</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Reason"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)}>Back</Button>
          <Button
            color="error"
            variant="contained"
            disabled={!!loading}
            onClick={() => run('cancel', async () => {
              await cancelGroceryOrder(order._id, { reason: cancelReason });
              setCancelOpen(false);
            })}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={refundOpen} onClose={() => setRefundOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Refund order</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 1, mb: 1.5 }}>
            {REFUND_ELIGIBILITY}
          </Alert>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {refundDestinationHint(order)}
            {customerId ? ' After refund, open the customer profile to verify the amount.' : ''}
          </Alert>
          <TextField fullWidth label="Reason" value={refundReason} onChange={(e) => setRefundReason(e.target.value)} sx={{ mb: 2 }} />
          <TextField
            fullWidth
            type="number"
            label={`Amount (max ${formatMoney(order.total ?? 0)})`}
            value={refundAmount}
            onChange={(e) => setRefundAmount(e.target.value)}
            helperText="Leave empty for full refund"
          />
          {userType === 'super_admin' && (
            <FormControlLabel
              sx={{ mt: 1 }}
              control={<Checkbox checked={overrideWindow} onChange={(e) => setOverrideWindow(e.target.checked)} />}
              label="Override refund window (reason above is recorded on the order)"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            disabled={!!loading || alreadyRefunded}
            onClick={() => run('refund', async () => {
              const amount = refundAmount ? Number(refundAmount) : undefined;
              const credited = amount ?? order.total ?? 0;
              await refundGroceryOrder(order._id, {
                reason: refundReason || undefined,
                amount,
                ...(overrideWindow ? { overrideRefundWindow: true, overrideReason: refundReason || undefined } : {}),
              });
              setRefundOpen(false);
              setOverrideWindow(false);
              setRefundAmount('');
              setRefundReason('');
              setLastRefundCustomerId(customerId);
              setRefundNotice(`Refund of ${formatMoney(credited)} processed.`);
            })}
          >
            Refund
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={assignOpen} onClose={() => setAssignOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Assign delivery partner</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Partner user ID" value={partnerId} onChange={(e) => setPartnerId(e.target.value)} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!!loading || !partnerId.trim()}
            onClick={() => run('assign', async () => {
              await assignGroceryDeliveryPartner(order._id, partnerId.trim());
              setAssignOpen(false);
              setPartnerId('');
            })}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
